import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  Stack,
  Chip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  People as PeopleIcon,
  Palette as PaletteIcon,
  VpnKey as VpnKeyIcon,
  Psychology as AgentIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TeamContactsManagement from '../components/TeamContactsManagement';
import ThemeAndBrandingSettings from '../components/ThemeAndBrandingSettings';
import SamlLoginSettings from '../components/SamlLoginSettings';
import AgentConfigurationSettings from '../components/AgentConfigurationSettings';
import { UI_TOKENS } from '../constants/uiTokens';
import { PAGE, CARD_SX, TABS_SX } from '../constants/pageStyles';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

type SettingsSection = 'sso' | 'theme' | 'team-contacts' | 'configure-agent';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState(0);

  const canManageTeamContacts = () => {
    return (
      user?.role === 'super_admin' ||
      user?.role === 'sso_admin' ||
      user?.role === 'iga_admin' ||
      user?.role === 'pam_admin'
    );
  };

  const canManageTheme = () => {
    return user?.role === 'super_admin';
  };

  const canManageSamlLogin = () => {
    return user?.role === 'super_admin' || user?.role === 'app_admin';
  };

  const canConfigureAgent = () => {
    return user?.role === 'super_admin';
  };

  const getAllowedTeamTypes = (): ('SSO' | 'PAM' | 'IGA')[] => {
    if (user?.role === 'super_admin') {
      return ['SSO', 'PAM', 'IGA'];
    }
    if (user?.role === 'sso_admin') {
      return ['SSO'];
    }
    if (user?.role === 'iga_admin') {
      return ['IGA'];
    }
    if (user?.role === 'pam_admin') {
      return ['PAM'];
    }
    return [];
  };

  const showSamlLoginTab = canManageSamlLogin();
  const showTheme = canManageTheme();
  const showTeam = canManageTeamContacts();
  const showConfigureAgent = canConfigureAgent();

  const sectionIndexes = useMemo(() => {
    const map: Partial<Record<SettingsSection, number>> = {};
    let i = 0;
    if (showSamlLoginTab) map.sso = i++;
    if (showTheme) map.theme = i++;
    if (showTeam) map['team-contacts'] = i++;
    if (showConfigureAgent) map['configure-agent'] = i++;
    return map;
  }, [showSamlLoginTab, showTheme, showTeam, showConfigureAgent]);

  const samlPanelIndex = sectionIndexes.sso ?? -1;
  const themePanelIndex = sectionIndexes.theme ?? -1;
  const teamPanelIndex = sectionIndexes['team-contacts'] ?? -1;
  const agentPanelIndex = sectionIndexes['configure-agent'] ?? -1;

  const focusTeamParam = searchParams.get('team');
  const focusTeam =
    focusTeamParam === 'SSO' || focusTeamParam === 'PAM' || focusTeamParam === 'IGA'
      ? focusTeamParam
      : undefined;
  const autoEditTeamInfo = searchParams.get('edit') === 'team-info';

  useEffect(() => {
    const section = searchParams.get('section') as SettingsSection | null;
    if (section && sectionIndexes[section] != null) {
      setSelectedTab(sectionIndexes[section]!);
      return;
    }
    if (focusTeam && teamPanelIndex >= 0) {
      setSelectedTab(teamPanelIndex);
    }
  }, [searchParams, sectionIndexes, focusTeam, teamPanelIndex]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    const section =
      (Object.entries(sectionIndexes).find(([, idx]) => idx === newValue)?.[0] as
        | SettingsSection
        | undefined) || null;
    const next = new URLSearchParams(searchParams);
    if (section) next.set('section', section);
    else next.delete('section');
    if (section !== 'team-contacts') {
      next.delete('team');
      next.delete('edit');
    }
    setSearchParams(next, { replace: true });
  };

  const visibleTabsCount = [showSamlLoginTab, showTheme, showTeam, showConfigureAgent].filter(Boolean).length;

  return (
    <Box sx={PAGE.padding}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.25}
        sx={{ mb: 2.5 }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            color: 'primary.main',
          }}
        >
          <SettingsIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={PAGE.title}>Settings</Typography>
          <Typography sx={PAGE.subtitle}>
            Manage SSO, theme &amp; branding, team contacts, and the VYSOR agent for your enterprise.
          </Typography>
        </Box>
        <Chip
          label={`${visibleTabsCount} section${visibleTabsCount === 1 ? '' : 's'} available`}
          size="small"
          variant="outlined"
          sx={{ height: 24, fontSize: UI_TOKENS.text.helper, fontWeight: 600 }}
        />
      </Stack>

      <Card variant="outlined" sx={CARD_SX}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover', px: 1.5 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={TABS_SX}
          >
            {showSamlLoginTab && (
              <Tab icon={<VpnKeyIcon sx={{ fontSize: 18 }} />} label="SSO" iconPosition="start" />
            )}
            {showTheme && (
              <Tab
                icon={<PaletteIcon sx={{ fontSize: 18 }} />}
                label="Theme & Branding"
                iconPosition="start"
              />
            )}
            {showTeam && (
              <Tab
                icon={<PeopleIcon sx={{ fontSize: 18 }} />}
                label="Team Contacts"
                iconPosition="start"
              />
            )}
            {showConfigureAgent && (
              <Tab
                icon={<AgentIcon sx={{ fontSize: 18 }} />}
                label="Configure Agent"
                iconPosition="start"
              />
            )}
          </Tabs>
        </Box>

        {showSamlLoginTab && samlPanelIndex >= 0 && (
          <TabPanel value={selectedTab} index={samlPanelIndex}>
            <SamlLoginSettings />
          </TabPanel>
        )}

        {showTheme && themePanelIndex >= 0 && (
          <TabPanel value={selectedTab} index={themePanelIndex}>
            <ThemeAndBrandingSettings />
          </TabPanel>
        )}

        {showTeam && teamPanelIndex >= 0 && (
          <TabPanel value={selectedTab} index={teamPanelIndex}>
            <TeamContactsManagement
              userRole={user?.role || 'user'}
              allowedTeamTypes={getAllowedTeamTypes()}
              focusTeam={focusTeam}
              autoEditTeamInfo={autoEditTeamInfo}
            />
          </TabPanel>
        )}

        {showConfigureAgent && agentPanelIndex >= 0 && (
          <TabPanel value={selectedTab} index={agentPanelIndex}>
            <AgentConfigurationSettings />
          </TabPanel>
        )}
      </Card>
    </Box>
  );
};

export default Settings;
